import { useCallback, useMemo, useState } from 'react';
import { Match } from 'effect';
import type { ProxyTypeEnum } from 'venn-api';
import type {
  BulkPickerDetailedProxyMetadata,
  LayeredDetailedProxyMetadata,
} from '../../../../hooks/useProxyDataQuery';
import { getExtrapolateFromValue, isExtrapolatableProxyType, type SecondLayerProxyType } from 'venn-utils';
import {
  createLayerableProxyInfo,
  createNonLayerableProxyInfo,
  createSecondLayerProxyInfo,
  deriveLayeredProxyConfig,
  type LayerableProxyInfo,
  LayeredProxyConfiguration,
  type NonLayerableProxyInfo,
} from '../proxy-types';

import type { FundToBulkProxy, FundToProxy } from '../../types';
import type { SelectedProxy } from '../utils';
import {
  type LayeredProxyPickerStateResult,
  ProxyPickerStateResult,
  type ProxyTabValue,
  type UseProxyPickerStateOptions,
} from './types';

const proxyConfigsMatch = (a: BulkPickerDetailedProxyMetadata, b: BulkPickerDetailedProxyMetadata): boolean =>
  a.proxyId === b.proxyId && a.proxyType === b.proxyType && a.extrapolate === b.extrapolate && a.numLags === b.numLags;

const getUniqueLayeredProxyConfig = (
  investments: (FundToProxy | FundToBulkProxy)[],
  proxyData: Record<string, LayeredDetailedProxyMetadata>,
): LayeredDetailedProxyMetadata | undefined => {
  if (investments.length === 0) return undefined;

  const firstInvestmentProxies = proxyData[investments[0]!.id] ?? ([] as const);

  const allMatch = investments.every((investment) => {
    const investmentProxies = proxyData[investment.id] ?? ([] as const);

    if (investmentProxies.length !== firstInvestmentProxies.length) {
      return false;
    }

    if (investmentProxies.length === 0) {
      return true;
    }

    if (investmentProxies.length === 1 && firstInvestmentProxies.length === 1) {
      return proxyConfigsMatch(investmentProxies[0], firstInvestmentProxies[0]);
    }

    if (investmentProxies.length === 2 && firstInvestmentProxies.length === 2) {
      return (
        proxyConfigsMatch(investmentProxies[0], firstInvestmentProxies[0]) &&
        proxyConfigsMatch(investmentProxies[1], firstInvestmentProxies[1])
      );
    }

    return false;
  });

  return allMatch ? firstInvestmentProxies : undefined;
};

const getProxyInfoFromValue = (proxy: LayerableProxyInfo | NonLayerableProxyInfo) => ({
  numLags: proxy.numLags,
  extrapolate: getExtrapolateFromValue(proxy),
  selectedProxy: proxy.selectedProxy,
});

export const getNewConfigForFirstLayerProxyType = (
  currentConfig: LayeredProxyConfiguration,
  proxyType: ProxyTypeEnum,
): LayeredProxyConfiguration => {
  const { extrapolate, numLags, selectedProxy } = Match.value(currentConfig).pipe(
    Match.tag('Empty', () => ({ numLags: 0, extrapolate: false, selectedProxy: undefined })),
    Match.tag('SingleLayerable', ({ proxy }) => getProxyInfoFromValue(proxy)),
    Match.tag('SingleNonLayerable', ({ proxy }) => getProxyInfoFromValue(proxy)),
    Match.tag('Multi', ({ firstLayer }) => getProxyInfoFromValue(firstLayer)),
    Match.exhaustive,
  );

  const overrides = { numLags, selectedProxy };

  if (proxyType === 'BACKFILL') {
    return LayeredProxyConfiguration.SingleLayerable({
      proxy: createLayerableProxyInfo(overrides),
    });
  }

  return LayeredProxyConfiguration.SingleNonLayerable({
    proxy: createNonLayerableProxyInfo(proxyType, extrapolate, overrides),
  });
};

interface UseLayeredProxyStateInternalOptions {
  investments: (FundToProxy | FundToBulkProxy)[];
  returnsProxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
}

const useLayeredProxyStateInternal = ({ investments, returnsProxyDataByFund }: UseLayeredProxyStateInternalOptions) => {
  const getAllProxiesForInvestment = useCallback(
    (investmentId: string): LayeredDetailedProxyMetadata => {
      return returnsProxyDataByFund[investmentId] ?? ([] as const);
    },
    [returnsProxyDataByFund],
  );

  const initialConfiguration = useMemo((): LayeredProxyConfiguration => {
    const uniqueProxies = getUniqueLayeredProxyConfig(investments, returnsProxyDataByFund);
    if (uniqueProxies === undefined) {
      return LayeredProxyConfiguration.Empty();
    }
    return deriveLayeredProxyConfig(uniqueProxies);
  }, [investments, returnsProxyDataByFund]);

  const [configuration, setConfiguration] = useState<LayeredProxyConfiguration>(initialConfiguration);
  const [activeTab, setActiveTab] = useState<ProxyTabValue>('first');

  const setFirstLayerProxyType = useCallback((proxyType: ProxyTypeEnum) => {
    setConfiguration((config) => getNewConfigForFirstLayerProxyType(config, proxyType));
  }, []);

  const setFirstLayerProxy = useCallback((proxy: SelectedProxy | null) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', (state) =>
          LayeredProxyConfiguration.SingleLayerable({
            proxy: { ...state.proxy, selectedProxy: proxy },
          }),
        ),
        Match.tag('SingleNonLayerable', (state) =>
          LayeredProxyConfiguration.SingleNonLayerable({
            proxy: { ...state.proxy, selectedProxy: proxy },
          }),
        ),
        Match.tag('Multi', (state) =>
          LayeredProxyConfiguration.Multi({
            ...state,
            firstLayer: { ...state.firstLayer, selectedProxy: proxy },
          }),
        ),
        Match.exhaustive,
      ),
    );
  }, []);

  const setFirstLayerNumLags = useCallback((numLags: number) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', (state) =>
          LayeredProxyConfiguration.SingleLayerable({ proxy: { ...state.proxy, numLags } }),
        ),
        Match.tag('SingleNonLayerable', (state) =>
          LayeredProxyConfiguration.SingleNonLayerable({ proxy: { ...state.proxy, numLags } }),
        ),
        Match.tag('Multi', (state) =>
          LayeredProxyConfiguration.Multi({ ...state, firstLayer: { ...state.firstLayer, numLags } }),
        ),
        Match.exhaustive,
      ),
    );
  }, []);

  const setFirstLayerExtrapolate = useCallback((extrapolate: boolean) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', ({ proxy }) => {
          if (extrapolate) {
            return LayeredProxyConfiguration.SingleNonLayerable({
              proxy: createNonLayerableProxyInfo('BACKFILL', true, {
                selectedProxy: proxy.selectedProxy,
                numLags: proxy.numLags,
              }),
            });
          }
          return config;
        }),
        Match.tag('SingleNonLayerable', ({ proxy }) => {
          if (!isExtrapolatableProxyType(proxy.proxyType)) return config;
          if (proxy.proxyType === 'BACKFILL' && !extrapolate) {
            return LayeredProxyConfiguration.SingleLayerable({
              proxy: createLayerableProxyInfo({
                selectedProxy: proxy.selectedProxy,
                numLags: proxy.numLags,
              }),
            });
          }
          return LayeredProxyConfiguration.SingleNonLayerable({
            proxy: createNonLayerableProxyInfo(proxy.proxyType, extrapolate, {
              selectedProxy: proxy.selectedProxy,
              numLags: proxy.numLags,
            }),
          });
        }),
        Match.tag('Multi', ({ firstLayer }) => {
          if (extrapolate) {
            return LayeredProxyConfiguration.SingleNonLayerable({
              proxy: createNonLayerableProxyInfo('BACKFILL', true, {
                selectedProxy: firstLayer.selectedProxy,
                numLags: firstLayer.numLags,
              }),
            });
          }
          return config;
        }),
        Match.exhaustive,
      ),
    );
  }, []);

  const setSecondLayerProxyType = useCallback((proxyType: SecondLayerProxyType, extrapolate: boolean) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', ({ proxy }) =>
          LayeredProxyConfiguration.Multi({
            firstLayer: proxy,
            secondLayer: createSecondLayerProxyInfo(proxyType, extrapolate),
          }),
        ),
        Match.tag('SingleNonLayerable', () => config),
        Match.tag('Multi', (state) =>
          LayeredProxyConfiguration.Multi({
            ...state,
            secondLayer: createSecondLayerProxyInfo(proxyType, extrapolate, {
              selectedProxy: state.secondLayer.selectedProxy,
            }),
          }),
        ),
        Match.exhaustive,
      ),
    );
    setActiveTab('second');
  }, []);

  const removeSecondLayer = useCallback(() => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', () => config),
        Match.tag('SingleNonLayerable', () => config),
        Match.tag('Multi', ({ firstLayer }) => LayeredProxyConfiguration.SingleLayerable({ proxy: firstLayer })),
        Match.exhaustive,
      ),
    );
    setActiveTab('first');
  }, []);

  const setSecondLayerProxy = useCallback((proxy: SelectedProxy | null) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', () => config),
        Match.tag('SingleNonLayerable', () => config),
        Match.tag('Multi', (state) =>
          LayeredProxyConfiguration.Multi({
            ...state,
            secondLayer: { ...state.secondLayer, selectedProxy: proxy },
          }),
        ),
        Match.exhaustive,
      ),
    );
  }, []);

  const setSecondLayerNumLags = useCallback((numLags: number) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', () => config),
        Match.tag('SingleNonLayerable', () => config),
        Match.tag('Multi', (state) =>
          LayeredProxyConfiguration.Multi({ ...state, secondLayer: { ...state.secondLayer, numLags } }),
        ),
        Match.exhaustive,
      ),
    );
  }, []);

  const setSecondLayerExtrapolate = useCallback((extrapolate: boolean) => {
    setConfiguration((config) =>
      Match.value(config).pipe(
        Match.tag('Empty', () => config),
        Match.tag('SingleLayerable', () => config),
        Match.tag('SingleNonLayerable', () => config),
        Match.tag('Multi', ({ firstLayer, secondLayer }) => {
          if (!isExtrapolatableProxyType(secondLayer.proxyType)) return config;
          return LayeredProxyConfiguration.Multi({
            firstLayer,
            secondLayer: createSecondLayerProxyInfo(secondLayer.proxyType, extrapolate, {
              selectedProxy: secondLayer.selectedProxy,
              numLags: secondLayer.numLags,
            }),
          });
        }),
        Match.exhaustive,
      ),
    );
  }, []);

  return {
    configuration,
    activeTab,
    setActiveTab,

    setFirstLayerProxyType,
    setFirstLayerProxy,
    setFirstLayerNumLags,
    setFirstLayerExtrapolate,

    setSecondLayerProxyType,
    removeSecondLayer,
    setSecondLayerProxy,
    setSecondLayerNumLags,
    setSecondLayerExtrapolate,

    getAllProxiesForInvestment,
  };
};

export const useLayeredProxyState = ({
  investments,
  returnsProxyDataByFund,
}: UseProxyPickerStateOptions): LayeredProxyPickerStateResult => {
  const {
    configuration,
    activeTab,
    setActiveTab,
    setFirstLayerProxyType,
    setFirstLayerProxy,
    setFirstLayerNumLags,
    setFirstLayerExtrapolate,
    setSecondLayerProxyType,
    removeSecondLayer,
    setSecondLayerProxy,
    setSecondLayerNumLags,
    setSecondLayerExtrapolate,
  } = useLayeredProxyStateInternal({
    investments,
    returnsProxyDataByFund,
  });

  return ProxyPickerStateResult.Layered({
    state: {
      configuration,
      activeTab,
    },
    mutations: {
      setActiveTab,
      setFirstLayerProxyType,
      setFirstLayerProxy,
      setFirstLayerNumLags,
      setFirstLayerExtrapolate,
      setSecondLayerProxyType,
      removeSecondLayer,
      setSecondLayerProxy,
      setSecondLayerNumLags,
      setSecondLayerExtrapolate,
    },
  });
};
